import { ContractStatus } from './types';
import i18n from '@/shared/i18n/config';

/**
 * Calcula o status de um contrato baseado nas datas.
 * Mantido apenas para cenários de mock/local.
 * @param startDate Data de início (ISO string)
 * @param endDate Data de fim (ISO string)
 * @returns Status calculado: 'active' | 'expiring' | 'expired'
 */
export const calculateContractStatus = (
  startDate: string,
  endDate: string
): ContractStatus => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Se ainda não começou ou já terminou
  if (now < start || now > end) {
    return 'expired';
  }

  // Calcula dias até a expiração
  const daysUntilExpiration = Math.ceil(
    (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Se faltam 30 dias ou menos
  if (daysUntilExpiration <= 30) {
    return 'expiring';
  }

  return 'active';
};

const STATUS_METADATA: Record<
  ContractStatus,
  { labelKey: string; color: string; border: string; badgeClass: string }
> = {
  DRAFT: {
    labelKey: 'contracts:draft',
    color: 'bg-slate-100 text-slate-700',
    border: 'border-slate-300',
    badgeClass: 'bg-slate-100 text-slate-700 border-slate-300',
  },
  PENDING_SIGNATURE: {
    labelKey: 'contracts:pendingSignature',
    color: 'bg-amber-100 text-amber-800',
    border: 'border-amber-300',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-300',
  },
  SIGNED: {
    labelKey: 'contracts:signed',
    color: 'bg-emerald-100 text-emerald-800',
    border: 'border-emerald-300',
    badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  },
  EXPIRED: {
    labelKey: 'contracts:expired',
    color: 'bg-gray-100 text-gray-800',
    border: 'border-gray-300',
    badgeClass: 'bg-gray-100 text-gray-800 border-gray-300',
  },
  CANCELLED: {
    labelKey: 'contracts:cancelled',
    color: 'bg-red-100 text-red-700',
    border: 'border-red-300',
    badgeClass: 'bg-red-100 text-red-700 border-red-300',
  },
  active: {
    labelKey: 'contracts:active',
    color: 'bg-green-100 text-green-800',
    border: 'border-green-300',
    badgeClass: 'bg-green-100 text-green-800 border-green-300',
  },
  expiring: {
    labelKey: 'contracts:expiring',
    color: 'bg-yellow-100 text-yellow-800',
    border: 'border-yellow-300',
    badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  },
  expired: {
    labelKey: 'contracts:expired',
    color: 'bg-gray-100 text-gray-800',
    border: 'border-gray-300',
    badgeClass: 'bg-gray-100 text-gray-800 border-gray-300',
  },
};

/**
 * Retorna a cor do badge baseado no status
 */
export const getStatusColor = (status: ContractStatus): string => {
  return STATUS_METADATA[status]?.color ?? 'bg-muted text-muted-foreground';
};

export const getStatusBadgeClass = (status: ContractStatus): string => {
  return STATUS_METADATA[status]?.badgeClass ?? 'bg-muted text-muted-foreground border-muted';
};

/**
 * Retorna o label traduzido do status
 */
export const getStatusLabel = (status: ContractStatus): string => {
  const labelKey = STATUS_METADATA[status]?.labelKey;
  if (labelKey) {
    return i18n.t(labelKey);
  }
  return status;
};
